import { Button, Spinner, Typography } from '@scale/scaleui';
import { useState, useEffect } from 'react';
import {
  GetHiddenUnitTestsFromTaskMetadataRequest,
  RunUnitTestsRequest,
  RunUnitTestsResponse,
} from '@scale/shared/types/chat';
import API from '@scale/frontend-lib/dashboard-api';
import { useMutation, useQuery } from 'react-query';
import {
  CodingUnitTestsGenerationConfig,
  renderRunResponse,
  Format,
} from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/UnitTestsGenerationForm';
import { TextCollectionField, UnitField } from '@scale/shared/types/params/textcollection';

interface RunHiddenTestsButtonProps {
  codingUnitTestsGenerationConfig?: CodingUnitTestsGenerationConfig;
  annotations: Record<string, any>;
  assignmentId: string | undefined;
  field: TextCollectionField;
  setSubmitCodeError?: (field_id: string, error: string) => void;
}

const RunHiddenTestsButton: React.FC<RunHiddenTestsButtonProps> = ({
  codingUnitTestsGenerationConfig,
  annotations,
  assignmentId = '',
  field,
  setSubmitCodeError,
}) => {
  const [runResponse, setRunResponse] = useState<RunUnitTestsResponse | undefined>(undefined);
  const [hiddenTestsHint, setHiddenTestsHint] = useState<string>('');
  const codeFieldId = codingUnitTestsGenerationConfig?.codeFieldId;
  const enableRunHiddenTests = codingUnitTestsGenerationConfig?.enableRunHiddenTests;

  const { data: hiddenUnitTests, isLoading: isLoadingHiddenUnitTests } = useQuery(
    ['hiddenUnitTests', assignmentId],
    async () => {
      const requestParams: GetHiddenUnitTestsFromTaskMetadataRequest = {
        assignmentId,
      };
      const response = await API.genai.getHiddenUnitTestsFromTaskMetadata(requestParams);
      if (response.testFormat) {
        setHiddenTestsHint(`${response.numTests ?? ''} ${response.testFormat} tests available`);
      }
      return response;
    },
  );

  const codeText = codeFieldId !== undefined && annotations[codeFieldId]?.response?.[0];
  const missingCode = !codeText;

  const { mutate: runHiddenTestsMutation, isLoading: isRunningTests } = useMutation({
    mutationFn: async () => {
      const codeAnnotation = codeFieldId && annotations[codeFieldId]?.response?.[0];
      const format =
        Object.values(Format).find(
          f => f.toLowerCase() === hiddenUnitTests?.testFormat.toLowerCase().trim(),
        ) || Format.PyTest;
      const language = hiddenUnitTests?.testLanguage ?? 'Python';
      const methodName = hiddenUnitTests?.methodName ?? '';

      const requestParams: RunUnitTestsRequest = {
        language,
        format,
        code: codeAnnotation.toString(),
        assignmentId,
        unitTests: '',
        methodName,
        hideOutput: true,
      };

      setRunResponse(undefined);
      const unitTestResults = await API.genai.runUnitTests(requestParams);
      return unitTestResults;
    },
    onSuccess: response => {
      setRunResponse(response);
    },
    onError: (error: any) => {
      setRunResponse({ error: `Error running unit tests: ${error.message}` });
    },
  });

  useEffect(() => {
    if (
      (field as UnitField).unit_test_pass_required ||
      (field as UnitField).unit_test_full_coverage_required
    ) {
      setSubmitCodeError?.(field.field_id, 'Please run hidden unit tests.');
    }
  }, [codeText, field, setSubmitCodeError]);

  useEffect(() => {
    let message = '';
    if ((field as UnitField).unit_test_pass_required) {
      if ((runResponse?.output?.passes ?? -1) <= 0 || (runResponse?.output?.fails ?? -1) !== 0) {
        message = 'Please pass all hidden unit tests.';
      }
    }
    if ((field as UnitField).unit_test_full_coverage_required) {
      if ((runResponse?.output?.coverage ?? 100) !== 100) {
        message = 'Please ensure 100% test coverage.';
      }
    }
    setSubmitCodeError?.(field.field_id, message);
  }, [runResponse, field, setSubmitCodeError]);

  return (
    <div className="space-y-2">
      <div className="flex items-center">
        <div className="ml-auto flex flex-col space-y-2">
          {isRunningTests && <Spinner size="xs" />}
          {enableRunHiddenTests && !isRunningTests && (
            <Button
              variant="secondary"
              className="flex justify-center"
              disabled={missingCode || isLoadingHiddenUnitTests}
              onClick={() => runHiddenTestsMutation()}
            >
              Run Hidden Tests
            </Button>
          )}
          {enableRunHiddenTests && (
            <Typography.Paragraph2 className="text-sm text-neutral-500 whitespace-pre-line">
              {hiddenTestsHint}
            </Typography.Paragraph2>
          )}
        </div>
      </div>
      {runResponse && renderRunResponse(runResponse)}
    </div>
  );
};

export default RunHiddenTestsButton;
